<template>
    <div class="w-full">
        <SessionExpiredBanner
            class="sticky top-0 z-[9999999]"
            @sign-in="openSignIn"
            @retry="retrySave"
        ></SessionExpiredBanner>
        <div
            v-if="loading"
            class="mt-96 flex w-full items-start justify-center"
        >
            <LoadingSpinner size="large" :delay="false"></LoadingSpinner>
        </div>
        <TopolEditor
            v-show="!loading"
            :options="topolOptions"
            :stage="props.stage"
            @onSave="callbacks.onSave"
            @onSaveAndClose="callbacks.onSaveAndClose"
            @onTestSend="callbacks.onTestSend"
            @onInit="callbacks.onInit"
            @onBlockSave="callbacks.onBlockSave"
            @onBlockRemove="callbacks.onBlockRemove"
            @onBlockEdit="callbacks.onBlockEdit"
            @onAlert="callbacks.onAlert"
            @onClose="callbacks.onClose"
            @onLoaded="callbacks.onLoaded"
            @onTemplateRename="callbacks.onRenameTemplate"
            @updateTestingEmailAddresses="callbacks.updateTestingEmailAddresses"
        ></TopolEditor>
    </div>
</template>

<script lang="ts" setup>
/**
 * The Email Editor v3 integration, kept byte for byte as it was before the v4
 * migration apart from the aliased package import above. Teams land here only
 * when `editor_v3` is switched on for them in Kiosk; Pages/Editor/index.vue picks.
 *
 * Deliberately not refactored: while both runtimes ship, this file's value is
 * that it diffs clean against the last release. It goes away with the flag.
 */
import {
    TopolEditor,
    TopolPlugin,
    ITopolOptions,
    ISavedBlock,
    INotification,
    IStage,
} from '@topol.io/editor-vue-v3';

import LoadingSpinner from '@components/Atomic/LoadingSpinner/LoadingSpinner.vue';
import SessionExpiredBanner from './components/SessionExpiredBanner.vue';

import { ref } from 'vue';
import { router } from '@inertiajs/vue3';
import axios from 'axios';

import useSavedBlocksService from './composables/useSavedBlocksService';
import useSendTestService from './composables/useSendTestService';
import { create as createNotification } from '@composables/useNotifications';
import useEditorSession, {
    EDITOR_REQUEST_HEADERS,
    markSaved,
} from '@composables/useEditorSession';

import useConfirm from '@composables/useConfirm';
import useFonts from '@/Pages/EditorPreferences/Fonts/composables/useFonts';
import useEditorTranslations from './composables/useEditorTranslations';
import { ITopolPageProps } from '@/types/TopolPageProps/TopolPageProps';
import ICurrentTeam from '@/types/Members/ICurrentTeam';
import usePusher from '@/composables/usePusher';
import useCookiebot from '@/composables/useCookiebot';
import { useGtm } from '@/composables/useGtm';
import { useSubscriptionLabel } from '@/composables/useSubscriptionLabel';

const { startEcho, stopEcho } = usePusher();
const { show } = useConfirm();

type CurrentUser = {
    id: number;
    name: string;
    profile_photo_path: string;
    profile_photo_url: string;
};

interface Props {
    apiKey: string;
    token: string;
    definition: string;
    saveUrl: string;
    pingUrl: string;
    backUrl: string;
    savedBlocks?: ISavedBlock[];
    saveSavedBlockUrl: string;
    /**
     * Set only where the editor talks to the SAVED_SECTIONS API directly, which
     * is what unlocks synced sections. Left unset (Kiosk) the editor keeps the
     * callback based saved blocks below.
     */
    savedSectionsUrl?: string;
    teamId?: number;
    /**
     * Set where the template is still exactly what "create from scratch"
     * produced, and nothing has been done to it yet.
     */
    startFromScratch?: boolean;
    /**
     * A template being written has a subject; a premade one in the library is
     * not addressed to anybody, so Kiosk leaves this unset.
     */
    enableSubjectLine?: boolean;
    premadeTemplatesUrl?: string;
    premadeTemplateCategoriesUrl?: string;
    premadeTemplateKeywordsUrl?: string;
    getAutosaveUrl: string;
    getConversationsUrl: string;
    getCommentsUrl: string;
    sendTestEmailUrl: string;
    createAutosaveUrl: string;
    getAutosavesUrl: string;
    theme: string;
    fontSizes: string;
    defaultFonts: string;
    customFonts: string;
    smartMergeTags: string;
    mergeTags: string;
    currentUser: CurrentUser;
    ownerId: string | number;
    templateId: number;
    language?: string;
    colors: string;
    title?: string;
    stage?: IStage;
    enableMergeTagPreview?: boolean;
    imageCompressionOptions: string;
    enableImageToTextRatio: boolean;
    chatAI: boolean;
    googlePromotionalAnnotation: boolean;
    // Widened from `'production' | 'develop'`: the value is a free-form team
    // feature flag, and the page now types it honestly for both runtimes.
    lambdaStage: string;
    userRole: string;
    getFeedsUrl: string;
    getProductsUrl: string;
    templateStatus: string | null;
    enableComments?: boolean;
    renameTemplate?: boolean;
    htmlMinified: boolean;
    enableSectionVariants: boolean;
    emailServiceProvider?: 'mailchimp'|'sparkpost';
    isSubscribed: boolean;
    testingEmails: string;
    teamUsers: string;
    multilingual: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    savedBlocks: () => [],
    savedSectionsUrl: undefined,
    teamId: undefined,
    startFromScratch: false,
    enableSubjectLine: false,
    premadeTemplatesUrl: undefined,
    premadeTemplateCategoriesUrl: undefined,
    premadeTemplateKeywordsUrl: undefined,
    language: 'en',
    title: '',
    stage: 'production',
    enableComments: true,
    renameTemplate: true,
});

const globalProps = computed(() => usePage<ITopolPageProps>().props);
const teamProps = computed(() => globalProps.value.team as ICurrentTeam);

/**
 * Options the runtime editor supports but the bundled @topol.io/editor 0.3.0
 * typings do not describe yet - the editor itself is loaded from the CDN, so it
 * runs ahead of the package. `syncedSectionsEnabled` is there under its previous
 * name (`syncedRowsEnable`), and `enableSubjectLine` not at all. Both aliases can
 * go once the package catches up.
 */
type TopolOptionsBeyondTypings = Omit<ITopolOptions, 'api'> & {
    syncedSectionsEnabled?: boolean;
    enableSubjectLine?: boolean;
    api?: ITopolOptions['api'] & {
        SAVED_SECTIONS?: string;
        PREMADE_TEMPLATES?: string;
        PREMADE_TEMPLATE_CATEGORIES?: string;
        PREMADE_TEMPLATES_KEYWORDS?: string;
    };
};

type TopolPluginWithSyncedSections = typeof TopolPlugin & {
    refreshSyncedSections?: () => void;
};

const syncedSectionsEnabled = computed(
    () => props.savedSectionsUrl !== undefined
);
const { hideCookieBot, showCookieBot } = useCookiebot();
const { pushEvent } = useGtm();
const { planName } = useSubscriptionLabel();

onMounted(() => {
    hideCookieBot();

    listenForComments();
    listenForSyncedSections();
    listenForActiveMembers();

    heartbeatTimer = setInterval(() => ping(), HEARTBEAT_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibilityChange);
});

onUnmounted(() => {
    showCookieBot();

    stopListeningForActiveMembers();
    stopListeningForComments();

    if (heartbeatTimer !== undefined) {
        clearInterval(heartbeatTimer);
    }
    document.removeEventListener('visibilitychange', onVisibilityChange);
});

type PresenceMember = {
    id: number;
    name: string;
};

const activeMembers = ref<PresenceMember[]>([]);

/**
 * Only worth showing where somebody else could actually turn up. Read from the
 * template's own team rather than the current one, so it stays right for a user
 * who is switched elsewhere.
 */
const hasTeammates = computed(() => {
    try {
        return (JSON.parse(props.teamUsers) as unknown[]).length > 0;
    } catch {
        return false;
    }
});

const publishActiveMembers = () => {
    TopolPlugin.setActiveMembers(
        activeMembers.value
            .filter((member) => member.id !== props.currentUser.id)
            .map((member) => member.name)
    );
};

/**
 * Presence, not co-editing: the editor has no idea who else is in a template
 * unless we tell it, and telling it does not stop two people saving over each
 * other. It only makes the collision visible.
 */
const listenForActiveMembers = () => {
    if (!hasTeammates.value) {
        return;
    }

    startEcho();

    window.Echo.join('template.' + props.templateId)
        .here((members: PresenceMember[]) => {
            activeMembers.value = members;
            publishActiveMembers();
        })
        .joining((member: PresenceMember) => {
            // Pusher keys presence members by id, so a second tab is the same
            // member rather than a second one - but guard anyway.
            if (!activeMembers.value.some((known) => known.id === member.id)) {
                activeMembers.value.push(member);
            }

            publishActiveMembers();
        })
        .leaving((member: PresenceMember) => {
            activeMembers.value = activeMembers.value.filter(
                (known) => known.id !== member.id
            );

            publishActiveMembers();
        });
};

const stopListeningForActiveMembers = () => {
    if (hasTeammates.value) {
        window.Echo?.leave('template.' + props.templateId);
    }
};

const listenForComments = () => {
    if (teamProps.value.personal_team === false) {
        startEcho();

        window.Echo.private(
            'template-conversations.' + props.templateId
        ).listen(
            '.App\\Topol\\Templates\\Events\\TemplateConversationUpdated',
            (e) => {
                if (e.comment_by !== props.currentUser.id) {
                    window.TopolPlugin.refreshComments(e.key);
                }
            }
        );
    }
};

/**
 * A teammate editing a synced section changes content sitting inside the
 * template that is open right now, so pull the new content in place instead of
 * leaving a stale copy on screen until the next reload.
 *
 * Unlike comments this is not gated on being in a shared team - a personal team
 * has synced sections too, and the same user can have the editor open twice.
 */
const listenForSyncedSections = () => {
    if (!syncedSectionsEnabled.value || props.teamId === undefined) {
        return;
    }

    startEcho();

    // Every subscribed editor refreshes, the author's included. Skipping the
    // author would have to key on the connection that made the change, and the
    // edit is sent by the editor iframe on its own origin, which carries no
    // socket id to skip by - so the only available filter is the user, and that
    // silently strands a second window belonging to the same person. Pulling
    // content the author just saved costs them nothing; leaving another window
    // stale is the thing this feature exists to prevent.
    window.Echo.private('team-synced-sections.' + props.teamId).listen(
        '.synced-section-changed',
        () => {
            (
                window.TopolPlugin as TopolPluginWithSyncedSections
            )?.refreshSyncedSections?.();
        }
    );
};

const stopListeningForComments = () => {
    stopEcho();
};

const { defaultFonts: allDefaultFonts } = useFonts();
const loading = ref(true);

const {
    createSavedBlock,
    deleteSavedBlock,
    editSavedBlock,
    prepareSavedBlocks,
} = useSavedBlocksService(
    props.savedBlocks,
    props.saveSavedBlockUrl,
    props.language ?? 'en'
);

/**
 * The subject the editor has on screen right now, which is not necessarily what
 * is saved - someone can type one and send a test before saving. The plugin
 * hands us the live definition on test send, so read it from there and let the
 * backend fall back to the saved copy when there is nothing to read.
 *
 * Multilingual templates keep one subject per language mutation; the primary is
 * the one a test send is about.
 */
const subjectFromDefinition = (json: unknown): string | undefined => {
    const definition = json as {
        attributes?: { 'mj-subject'?: { text?: string } };
        langs?: {
            primary?: boolean;
            translations?: { 'mj-subject'?: { content?: string } };
        }[];
    } | null;

    const mutation =
        definition?.langs?.find((lang) => lang.primary) ?? definition?.langs?.[0];

    const subject =
        mutation?.translations?.['mj-subject']?.content ??
        definition?.attributes?.['mj-subject']?.text;

    return subject?.trim() || undefined;
};

const { sendTestEmail } = useSendTestService(props.sendTestEmailUrl);

const { translations: editorTranslations, getTranslations } =
    useEditorTranslations(props.language ?? 'en');

// @ts-ignore
const translations = ref(editorTranslations[props.language ?? 'en']);

const { ping, isRetrying } = useEditorSession(props.pingUrl);

const HEARTBEAT_INTERVAL_MS = 60_000;
let heartbeatTimer: ReturnType<typeof setInterval> | undefined;

const onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
        ping();
    }
};

const saveDefinition = (json: unknown, html: unknown) =>
    axios.post(
        props.saveUrl,
        {
            definition: JSON.stringify(json),
            html: html,
        },
        { headers: EDITOR_REQUEST_HEADERS }
    );

const openSignIn = (): void => {
    window.open(route('login'), '_blank');
};

const retrySave = async (): Promise<void> => {
    if (isRetrying.value) {
        return;
    }

    isRetrying.value = true;

    const stillValid = await ping();

    if (!stillValid) {
        createNotification({
            title: 'Still signed out — sign in first.',
            type: 'error',
        });

        isRetrying.value = false;

        return;
    }

    // Re-fire onSave with the CURRENT editor content rather than replaying a
    // stale snapshot captured at the original failed save. isRetrying stays set
    // until onSave settles the re-fired save (see its `finally`), so a second
    // click can't trigger a duplicate concurrent save.
    TopolPlugin.save();
};

const getEditorRole = () => {
    switch (props.userRole) {
        case 'owner':
        case 'admin':
            return 'manager';
        case 'editor':
            return 'editor';
        case 'viewer':
            return 'reader';
        default:
            return 'reader';
    }
};

const callbacks = {
    async onSave({ json, html }: { json: unknown; html: unknown }) {
        try {
            await saveDefinition(json, html);

            markSaved();

            createNotification({
                title: translations.value.saveSuccess,
                type: 'success',
            });
        } catch (e) {
            // A 401/419 has already raised the session banner via the axios
            // interceptor; any other error is toasted there too. Either way we
            // must not show a false "Saved!" message.
        } finally {
            // Release the retry guard once the (possibly re-fired) save settles.
            isRetrying.value = false;
        }
    },

    async updateTestingEmailAddresses(emails: string[]) {
        await axios.put(
            route('editor-preferences.testing-emails.update'),
            {
                emails: emails,
            },
            { headers: EDITOR_REQUEST_HEADERS }
        );
    },

    async onSaveAndClose({ json, html }: { json: unknown; html: unknown }) {
        try {
            await saveDefinition(json, html);

            markSaved();

            router.visit(props.backUrl);
        } catch (e) {
            // Session expired or the save failed - stay in the editor so the
            // banner can warn the user instead of navigating away from unsaved
            // work.
        }
    },

    async onBlockSave(json: ISavedBlock) {
        const newSavedBlocks = await createSavedBlock(json);

        if (newSavedBlocks !== undefined) {
            TopolPlugin.setSavedBlocks(newSavedBlocks);

            createNotification({
                title: translations.value.saveBlockSuccess,
                text: ``,
                type: 'success',
            });
        }
    },

    async onBlockRemove(templateSavedBlockId: number) {
        const newSavedBlocks = await deleteSavedBlock(templateSavedBlockId);

        if (newSavedBlocks !== undefined) {
            TopolPlugin.setSavedBlocks(newSavedBlocks);

            createNotification({
                title: translations.value.removeBlockSuccess,
                text: ``,
                type: 'success',
            });
        }
    },

    async onBlockEdit(templateSavedBlockId: number) {
        const newSavedBlocks = await editSavedBlock(templateSavedBlockId);

        if (newSavedBlocks !== undefined) {
            TopolPlugin.setSavedBlocks(newSavedBlocks);

            createNotification({
                title: translations.value.updateBlockSuccess,
                text: ``,
                type: 'success',
            });
        }
    },

    async onTestSend({
        email,
        json,
        html,
    }: {
        email: string[];
        json: unknown;
        html: unknown;
    }) {
        if (!props.isSubscribed) {
            createNotification({
                title: 'Email not sent',
                text: `You need to be a paid user to send test emails. Please select an appropriate ${planName.value} subscription to continue.`,
                type: 'error',
            });

            return;
        }

        try {
            const res = await sendTestEmail(
                email,
                html,
                subjectFromDefinition(json)
            );

            if (res.data.success === true) {
                createNotification({
                    title: translations.value.sentEmailSuccessTitle,
                    text: getTranslations('sentEmailSuccess', email),
                    type: 'success',
                });
                pushEvent('topolpro_send_test_link');
            } else {
                createNotification({
                    title: translations.value.sentEmailFailedTitle,
                    text: getTranslations('sentEmailFailed', email),
                    type: 'error',
                });
            }
        } catch (err) {
            createNotification({
                title: translations.value.sentEmailFailedTitle,
                text: getTranslations('sentEmailFailed', email),
                type: 'error',
            });
        }
    },

    onAlert(notification: INotification) {
        if (!notification.expectSideEffect) {
            createNotification({
                title: notification.title,
                text: notification.text,
                type: notification.type,
                persistent: notification.persistent,
            });
        }
    },

    onInit() {
        // Loading anything replaces the default the editor just built for
        // itself, and that default is precisely what it compares against to
        // decide whether to offer the premade picker. A scratch template has
        // nothing worth keeping - it is a stored blank - so leaving it unloaded
        // costs nothing and lets the editor make the offer.
        if (props.startFromScratch) {
            // onLoaded is what normally reveals the editor, and it only fires in
            // answer to a load. Nothing is being loaded here, so it never will -
            // reveal it now instead. The plugin is ready by the time onInit runs.
            loading.value = false;

            return;
        }

        TopolPlugin.load(props.definition);
    },

    onLoaded() {
        loading.value = false;
    },

    async onClose() {
        const confirmed = await show({
            type: 'danger',
            header: translations.value.closeEditorTitle,
            message: translations.value.closeEditorText,
            confirm: translations.value.closeEditorConfirm,
            cancel: translations.value.cancel,
        });

        if (confirmed) {
            router.visit(props.backUrl);
        }
    },
    async onRenameTemplate() {
        const newTemplateName = await show({
            header: translations.value.templateRenameTitle,
            hasInput: true,
            inputData: {
                label: translations.value.templateRenameLabel,
                defaultValue: props.title,
            },
            confirm: translations.value.rename,
            cancel: translations.value.cancel,
        });

        if (typeof newTemplateName === 'string') {
            try {
                const result = await axios.put(
                    route('templates.update', {
                        template: props.templateId,
                    }),
                    {
                        name: newTemplateName,
                        status: props.templateStatus,
                    },
                    { headers: EDITOR_REQUEST_HEADERS }
                );
                if (result.data.success) {
                    createNotification({
                        title: translations.value.templateRenameSuccessTitle,
                        type: 'success',
                    });

                    window.TopolPlugin.setTemplateName(newTemplateName);
                    router.reload({ only: ['title'] });
                } else {
                    createNotification({
                        title: translations.value.templateRenameFailedTitle,
                        type: 'error',
                    });
                }
            } catch (e) {
                return;
            }
        }
    },
};

const topolOptions: TopolOptionsBeyondTypings = {
    apiAuthorizationHeader: 'Bearer ' + props.token,

    title: props.title,

    authorize: {
        apiKey: props.apiKey,
        userId: props.ownerId,
    },

    currentUser: {
        userId: props.currentUser.id,
        name: props.currentUser.name,
        profilePhotoUrl:
            props.currentUser.profile_photo_path ??
            props.currentUser.profile_photo_url,
    },

    role: getEditorRole(),

    enableAutosaves: true,

    // `true` hands the whole saved block panel over to the SAVED_SECTIONS API,
    // which is where synced sections live. An array keeps the old callback mode.
    savedBlocks: syncedSectionsEnabled.value
        ? true
        : prepareSavedBlocks(props.savedBlocks),
    syncedSectionsEnabled: syncedSectionsEnabled.value,

    // The subject rides inside the template JSON, so it arrives through the
    // existing onSave with no plumbing of its own - per language mutation too,
    // since the editor already runs multilingual.
    //
    // Asked for by the surface rather than inferred from something else that
    // happens to be set: whether a template has a subject is a fact about the
    // editor you are in, not about which urls it was handed.
    enableSubjectLine: props.enableSubjectLine,

    // Offers "start from a predefined template" instead of a blank canvas, and
    // only where there is somewhere to read them from: the Kiosk editor shares
    // this component and is given no url, so switching it on unconditionally
    // handed that editor a picker that could only apologise.
    //
    // premadeTemplatesOptions stays at its defaults for a related reason -
    // showDelete surfaces a Delete button wired to a Remove Template endpoint
    // that does not exist here.
    premadeTemplates: props.premadeTemplatesUrl !== undefined,

    api: {
        AUTOSAVES: props.getAutosavesUrl,
        GET_AUTOSAVE: props.getAutosaveUrl,
        AUTOSAVE: props.createAutosaveUrl,
        CONVERSATIONS: props.getConversationsUrl,
        COMMENTS: props.getCommentsUrl,
        FEEDS: props.getFeedsUrl,
        PRODUCTS: props.getProductsUrl,
        ...(props.savedSectionsUrl
            ? { SAVED_SECTIONS: props.savedSectionsUrl }
            : {}),
        // Without these the picker has nowhere to read from and says so, in a
        // toast, instead of offering anything.
        ...(props.premadeTemplatesUrl
            ? { PREMADE_TEMPLATES: props.premadeTemplatesUrl }
            : {}),
        ...(props.premadeTemplateCategoriesUrl
            ? {
                  PREMADE_TEMPLATE_CATEGORIES:
                      props.premadeTemplateCategoriesUrl,
              }
            : {}),
        ...(props.premadeTemplateKeywordsUrl
            ? {
                  PREMADE_TEMPLATES_KEYWORDS:
                      props.premadeTemplateKeywordsUrl,
              }
            : {}),
    },

    windowBar: ['close', 'fullscreen'],

    smartMergeTags: JSON.parse(props.smartMergeTags),
    mergeTags: JSON.parse(props.mergeTags),

    mobileFirstEnabled: true,

    //we handle alerts on our own, to be able reflect our server request responses statuses
    disableAlerts: true,

    language: props.language,
    fontSizes: JSON.parse(props.fontSizes),

    theme: {
        preset: props.theme === 'light' ? 'light' : 'dark',
        colors: {
            active: '#FFC000',
            'active-light': '#FFC000',
        },
    },
    colors: JSON.parse(props.colors),

    betaFeatures: {
        carousel: true,
    },

    customFonts:
        props.customFonts === '[]' && props.defaultFonts === '[]'
            ? {
                  override: false,
                  fonts: [],
              }
            : {
                  override: true,
                  fonts:
                      props.defaultFonts === '[]'
                          ? allDefaultFonts.concat(
                                JSON.parse(props.customFonts)
                            )
                          : JSON.parse(props.defaultFonts).concat(
                                JSON.parse(props.customFonts)
                            ),
              },

    enableMergeTagPreview: props.enableMergeTagPreview ?? false,
    imageCompressionOptions: JSON.parse(props.imageCompressionOptions),
    enableImageToTextRatio: props.enableImageToTextRatio,
    chatAI: props.chatAI,
    googlePromotionalAnnotation: props.googlePromotionalAnnotation,
    lambdaStage: props.lambdaStage,
    stage: props.stage,
    enableComments: props.enableComments,
    renameTemplate: props.renameTemplate,
    htmlMinified: props.htmlMinified,
    enableSectionVariants: props.enableSectionVariants,
    emailServiceProvider: props.emailServiceProvider,
    imageEditorV2: true,
    testingEmails: props.testingEmails ? JSON.parse(props.testingEmails) : [],
    teamUsers: JSON.parse(props.teamUsers),
    multilingual: props.multilingual,
    enableDarkMode: true,
};
</script>
